import { useContext, useState, useEffect } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import moment from "moment";

import MUIButton from "@mui/material/Button";
import MUIGrid from "@mui/material/Grid";
import MUIBox from "@mui/material/Box";
import MUITextField from "@mui/material/TextField";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUIIconButton from "@mui/material/IconButton";
import MUIHelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MUITooltip from "@mui/material/Tooltip";
import MUITypography from "@mui/material/Typography";
import { DatePicker as MUIDatePicker } from "@mui/x-date-pickers/DatePicker";
import MUIRepeatIcon from "@mui/icons-material/Repeat";
import Card from "../../Card";
import InfoItem from "../../InfoItem";

/**
 * Frequency Card renders a Card component displaying recurring order information (frequency and next frequency date)
 * @returns {JSX.Element}
 */

const title = "Frequency";
const icon = MUIRepeatIcon;

const className = "CardChartFrequency";

export default function CardChartFrequency(props) {
  const chartContext = useContext(ChartContext);

  const [editMode, setEditMode] = useState(false);
  const [cardChart, setCardChart] = useState(null);

  const [newRecurringOrderId, setNewRecurringOrderId] = useState(null);

  const recurringOrder = Object.values(
    chartContext.chart.recurringOrders || {},
  ).find(
    (recurringOrder) =>
      recurringOrder.orderId === chartContext.orderId ||
      recurringOrder.id === newRecurringOrderId,
  );

  // Close edit mode if originalChart changes (on save)
  useEffect(() => {
    setEditMode(false);
    setCardChart(null);
  }, [chartContext.originalChart]);

  // Sync this card's edit mode with ChartContext for ChartNavigationBar display
  useEffect(() => {
    chartContext.setCardEditMode?.(className, editMode);
    return () => chartContext.setCardEditMode?.(className, false);
  }, [editMode, chartContext.setCardEditMode]);

  // Set new recurring order id
  useEffect(() => {
    setNewRecurringOrderId(chartContext.getNegativeId());
  }, []);

  // Update validation rules
  useEffect(() => {
    if (editMode && recurringOrder) {
      const rules = [
        {
          target: "recurringOrders",
          id: recurringOrder?.id,
          field: "frequency",
          rule: {
            required: true,
          },
        },
      ];

      // Only require nextDate if frequency is set
      if (recurringOrder?.frequency) {
        rules.push({
          target: "recurringOrders",
          id: recurringOrder?.id,
          field: "nextDate",
          rule: {
            required: true,
          },
        });
      }

      chartContext.updateValidationRules({
        key: className,
        rules: rules,
      });
    } else {
      chartContext.updateValidationRules({ key: className, rules: [] });
    }
  }, [editMode, recurringOrder]);

  useEffect(() => {
    // If in edit mode and no recurring order, create a new one
    if (editMode && (!recurringOrder || recurringOrder.id <= 0)) {
      const newRecurringOrder = {
        id: newRecurringOrderId,
        orderId: chartContext.orderId,
        frequency: null,
        nextDate: null,
        name:
          chartContext.chart.patient[chartContext.patientId]?.firstName +
            " " +
            chartContext.chart.patient[chartContext.patientId]?.lastName ||
          null,
      };
      chartContext.onChange({
        recurringOrders: { [newRecurringOrderId]: newRecurringOrder },
      });
    }
    // If not in edit mode and new recurring order id exists, delete the new recurring order
    else if (
      !editMode &&
      newRecurringOrderId &&
      chartContext.chart.recurringOrders[newRecurringOrderId]
    ) {
      delete chartContext.chart.recurringOrders[newRecurringOrderId];
      chartContext.onChange({
        recurringOrders: { ...chartContext.chart.recurringOrders },
      });
    }
  }, [editMode]);

  /**
   * Renders the edit view displaying patient approval information.
   * @returns {JSX.Element} Grid layout with patient information display
   */
  const renderEditView = () => {
    const getFrequencyOptions = () => {
      return [
        { value: "None / Disabled", label: "None / Disabled" },
        { value: "Monthly", label: "Monthly" },
        { value: "Annually", label: "Annually" },
      ];
    };

    return (
      <MUIGrid
        container
        spacing={2}
        rowSpacing={3}
        sx={{ containerType: "inline-size" }}
      >
        <MUIGrid item xs={12} md={6} lg={3}>
          <MUIAutocomplete
            options={getFrequencyOptions()}
            value={
              getFrequencyOptions().find(
                (option) => option.value === recurringOrder?.frequency,
              ) || null
            }
            isOptionEqualToValue={(option, value) => {
              return option?.value === value?.value;
            }}
            getOptionLabel={(option) => option?.label || ""}
            onChange={(e, newValue) => {
              chartContext.onChange({
                recurringOrders: {
                  [recurringOrder?.id]: { frequency: newValue?.value || null },
                },
              });
              setCardChart({
                ...cardChart,
                frequency: newValue?.value || null,
              });
            }}
            renderInput={(params) => (
              <MUITextField
                {...params}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  ...params.InputProps,
                }}
                size="small"
                variant="standard"
                label="Frequency"
                fullWidth
                required={true}
                error={
                  chartContext.validationIssues[className]?.recurringOrders?.[
                    recurringOrder?.id
                  ]?.frequency
                    ? true
                    : false
                }
                helperText={
                  chartContext.validationIssues[className]?.recurringOrders?.[
                    recurringOrder?.id
                  ]?.frequency
                    ? chartContext.validationIssues[className]
                        ?.recurringOrders?.[recurringOrder?.id]?.frequency
                    : ""
                }
              />
            )}
          />
        </MUIGrid>
        <MUIGrid item xs={12} md={6} lg={3}>
          <MUIDatePicker
            label="Next Frequency Date"
            defaultValue={
              recurringOrder?.nextDate ? moment(recurringOrder.nextDate) : null
            }
            slotProps={{
              field: {
                clearable: false,
              },
              textField: {
                fullWidth: true,
                size: "small",
                variant: "standard",
                required: true,
                error: chartContext.validationIssues?.[className]
                  ?.recurringOrders?.[recurringOrder?.id || newRecurringOrderId]
                  ?.nextDate
                  ? true
                  : false,
                helperText:
                  chartContext.validationIssues?.[className]?.recurringOrders?.[
                    recurringOrder?.id || newRecurringOrderId
                  ]?.nextDate || "",
                InputLabelProps: {
                  shrink: true,
                },
              },
            }}
            onChange={(value) => {
              chartContext.onChange({
                recurringOrders: {
                  [recurringOrder.id]: {
                    nextDate: value ? moment(value).format("M/D/YYYY") : null,
                  },
                },
              });
              setCardChart({
                ...cardChart,
                nextDate: value ? moment(value).format("M/D/YYYY") : null,
              });
            }}
            views={["year", "month", "day"]}
          />
        </MUIGrid>
      </MUIGrid>
    );
  };

  /**
   * Renders the read-only view displaying approval information.
   * @returns {JSX.Element} Grid layout with approval information display
   */
  const renderReadOnlyView = () => {
    return (
      <MUIBox>
        <MUIGrid
          container
          spacing={4}
          rowSpacing={3}
          sx={{ containerType: "inline-size" }}
        >
          <MUIGrid item xs="auto">
            <InfoItem label="Frequency" value={recurringOrder?.frequency} />
          </MUIGrid>
          {recurringOrder?.nextDate && (
            <MUIGrid item xs="auto">
              <InfoItem
                label="Next Frequency Date"
                value={moment(recurringOrder?.nextDate).format("M/D/YYYY")}
              />
            </MUIGrid>
          )}
        </MUIGrid>
      </MUIBox>
    );
  };

  /**
   * Determines which view to render based on edit mode state. Renders search
   * box, add note field, notes table, or empty state.
   * @returns {JSX.Element} Either edit view or read-only view
   */
  const renderContent = () => {
    if (editMode) {
      return renderEditView();
    }
    return renderReadOnlyView();
  };

  /**
   * Returns the action buttons for the card component.
   * @returns {Array} Array of action button components
   */
  const getActions = () => {
    const handleCancel = () => {
      const updatedChart = { ...chartContext.chart };
      const updatedRecurringOrder = { ...recurringOrder };
      const originalRecurringOrder = Object.values(
        chartContext.originalChart?.recurringOrders || {},
      ).find(
        (recurringOrder) => recurringOrder.orderId === chartContext.orderId,
      );
      const recurringOrderInfoFields = cardChart ? Object.keys(cardChart) : [];

      for (const field of recurringOrderInfoFields) {
        if (
          recurringOrder.id > 0 &&
          recurringOrder[field] !== originalRecurringOrder[field]
        ) {
          updatedRecurringOrder[field] = originalRecurringOrder[field];
        }
        if (newRecurringOrderId) {
          delete updatedChart.recurringOrders[newRecurringOrderId];
          chartContext.onChange({
            recurringOrders: { ...updatedChart.recurringOrders },
          });
        }
      }

      chartContext.onChange({
        recurringOrders: { [recurringOrder?.id]: { ...updatedRecurringOrder } },
      });
      chartContext.setCardEditMode?.(className, false);
      setEditMode(false);

      const clearedIssues = { ...chartContext.validationIssues };
      delete clearedIssues[className];
      chartContext.updateValidationIssues(clearedIssues);
      setCardChart(null);
    };

    return [
      ...(editMode
        ? [
            <MUIButton
              color="error"
              disabled={chartContext.isUpdating}
              onClick={handleCancel}
              size="small"
            >
              Cancel
            </MUIButton>,
          ]
        : [
            <MUIButton
              disabled={chartContext.isUpdating}
              onClick={() => setEditMode(true)}
              size="small"
            >
              Edit
            </MUIButton>,
          ]),
    ];
  };

  return (
    <Card
      renderContent={renderContent}
      getActions={getActions}
      getTitle={() => (
        <>
          {title}
          {editMode && (
            <MUITooltip
              title={
                <MUITypography variant="caption">
                  Set the date for the first recurring order to be created. The
                  system will automatically generate orders on that date and
                  continue at the specified frequency. To stop future orders
                  from being created, set the frequency to "None / Disabled".
                </MUITypography>
              }
              arrow
              placement="right-start"
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 600,
                  },
                },
              }}
            >
              <MUIIconButton size="small" sx={{ ml: 0.5 }}>
                <MUIHelpOutlineIcon sx={{ fontSize: 16 }} />
              </MUIIconButton>
            </MUITooltip>
          )}
        </>
      )}
      getIcon={() => icon}
    />
  );
}

CardChartFrequency.meta = { className: className, title: title, icon: icon };
